import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createProgramaSchema } from "@/lib/validaciones/createProgramaSchema";
import type { CreateProgramaInput } from "@/lib/validaciones/createProgramaSchema";
import { useRegisterPrograma } from "@/hooks/programas/useRegisterPrograma";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SelectClienteFinal } from "@/components/ui/SelectClienteFinal";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { useCurrentUser } from "@/features/login/hooks/useCurrentUser";
import { Brain, Bot } from "lucide-react";

export function RegisterProgramaForm({
  setOpen,
  queryKey,
}: {
  setOpen: (open: boolean) => void;
  queryKey?: string[];
}) {
  const { user } = useCurrentUser();
  const isSuperAdmin = user?.rol?.nombre === "SUPER_ADMIN";
  const isIntermedio = user?.rol?.nombre === "INTERMEDIO";

  const form = useForm<
    Omit<CreateProgramaInput, "cerebro"> & { limiteIntentos?: number }
  >({
    resolver: zodResolver(createProgramaSchema),
    defaultValues: {
      idClienteFinal: 0,
      nombre: "",
      tono: "Tono Neutral / Global",
      rol: "",
      restricciones: "",
      formato: "",
      // cupoMaximo: 0,
      limiteIntentos: 3,
      fechaInicio: new Date(),
      fechaFin: new Date(),
      contexto_coach: null,
      rol_coach: null,
      tarea_coach: null,
      restricciones_criterios_coach: null,
    },
  });

  const { mutate, isPending } = useRegisterPrograma({ queryKey });

  const onSubmit = (data: Omit<CreateProgramaInput, "cerebro">) => {
    const finalData = {
      ...data,
      // cupoMaximo: isSuperAdmin ? data.cupoMaximo : 0,
      limiteIntentos: isSuperAdmin ? data.limiteIntentos : 3,
    };
    mutate(finalData, {
      onSuccess: () => {
        setOpen(false);
        form.reset();
      },
    });
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto pr-2"
      >
        {/* ── DATOS DEL PROGRAMA ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          <FormField
            control={form.control}
            name="idClienteFinal"
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel className="text-sm font-medium" required>
                  Cliente Final
                </FormLabel>
                <FormControl>
                  <SelectClienteFinal
                    value={
                      field.value ? { value: field.value, label: "" } : null
                    }
                    onChange={(option) =>
                      field.onChange(option ? option.value : 0)
                    }
                    idUsuarioAlta={isIntermedio ? user?.id : undefined}
                    placeholder="Selecciona un cliente..."
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="nombre"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-sm font-medium" required>
                  Nombre del Programa
                </FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value === "" ? null : value);
                    }}
                    placeholder="Ej: Programa de Capacitación BBVA"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {isSuperAdmin && (
            <FormField
              control={form.control}
              name="limiteIntentos"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium">
                    Límite de Intentos Permitidos
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      placeholder="Ej: 3"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {["fechaInicio", "fechaFin"].map((name) => (
            <FormField
              key={name}
              control={form.control}
              name={name as "fechaInicio" | "fechaFin"}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-medium" required>
                    {name === "fechaInicio"
                      ? "Inicio del Programa"
                      : "Fin del Programa"}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      value={field.value.toISOString().split("T")[0]}
                      onChange={(e) => field.onChange(new Date(e.target.value))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}
        </div>

        {/* ── CONFIGURACIÓN DE INTELIGENCIA ARTIFICIAL ── */}
        {!isIntermedio && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-2">
                Configuración de Inteligencia Artificial
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Columna izquierda: Cerebro del Programa */}
              <div className="rounded-lg border bg-muted/30 p-4 space-y-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Brain className="h-4 w-4 text-primary" />
                    <span className="text-sm font-semibold">
                      Cerebro del Programa
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Controla cómo la IA evalúa y estructura sus respuestas de
                    retroalimentación.
                  </p>
                </div>

                {(
                  [
                    {
                      name: "restricciones" as const,
                      label: "Restricciones de respuesta",
                      placeholder:
                        "Especifica las restricciones, límites o condiciones que debe respetar la IA al generar retroalimentación...",
                      description:
                        "Qué debe y no debe hacer la IA en sus respuestas.",
                    },
                    {
                      name: "formato" as const,
                      label: "Formato de salida",
                      placeholder:
                        "Describe la estructura, secciones y formato esperado de la retroalimentación...",
                      description: "Cómo debe estructurar la IA su respuesta.",
                    },
                  ] as const
                ).map(({ name, label, placeholder, description }) => (
                  <FormField
                    key={name}
                    control={form.control}
                    name={name}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">
                          {label}
                        </FormLabel>
                        <p className="text-xs text-muted-foreground -mt-1 mb-1">
                          {description}
                        </p>
                        <FormControl>
                          <Textarea
                            {...field}
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              field.onChange(value === "" ? null : value);
                            }}
                            placeholder={placeholder}
                            className="min-h-[100px] max-h-[220px] resize-y overflow-y-auto text-sm"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ))}
              </div>

              {/* Columna derecha: Coach IA */}
              <div className="rounded-lg border bg-muted/30 p-4 space-y-4">
                <div>
                  <div className="flex items-center gap-2">
                    <Bot className="h-4 w-4 text-primary" />
                    <span className="text-sm font-semibold">Coach IA</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Configura la personalidad, rol y criterios del coach de
                    inteligencia artificial.
                  </p>
                </div>

                {(
                  [
                    {
                      name: "contexto_coach" as const,
                      label: "Contexto",
                      placeholder:
                        "Describe el contexto del programa, sector, tipo de aprendices y objetivos generales que el coach debe conocer...",
                      description:
                        "Información del entorno que el coach debe tener en cuenta.",
                    },
                    {
                      name: "rol_coach" as const,
                      label: "Rol del Coach",
                      placeholder:
                        "Define quién es el coach: su perfil, especialidad y enfoque de acompañamiento...",
                      description: "Personalidad y perfil del coach IA.",
                    },
                    {
                      name: "tarea_coach" as const,
                      label: "Tarea",
                      placeholder:
                        "Describe la misión principal del coach: qué debe lograr en cada interacción con el aprendiz...",
                      description: "Objetivo central del coach en cada sesión.",
                    },
                    {
                      name: "restricciones_criterios_coach" as const,
                      label: "Restricciones y criterios de evaluación",
                      placeholder:
                        "Define los criterios de evaluación y comportamientos que el coach debe seguir o evitar...",
                      description: "Reglas y estándares que rigen al coach.",
                    },
                  ] as const
                ).map(({ name, label, placeholder, description }) => (
                  <FormField
                    key={name}
                    control={form.control}
                    name={name}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium">
                          {label}
                        </FormLabel>
                        <p className="text-xs text-muted-foreground -mt-1 mb-1">
                          {description}
                        </p>
                        <FormControl>
                          <Textarea
                            {...field}
                            value={field.value ?? ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              field.onChange(value === "" ? null : value);
                            }}
                            placeholder={placeholder}
                            className="min-h-[80px] max-h-[150px] resize-y overflow-y-auto text-sm"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        <Button
          type="submit"
          disabled={isPending}
          className="w-full h-10 sm:h-11 text-sm sm:text-base"
        >
          {isPending ? "Creando..." : "Crear Programa"}
        </Button>
      </form>
    </Form>
  );
}
